import { create } from 'zustand';
import { storeNotice } from '@/shared/lib/store-notice';
import { coreApi } from '@/integrations/api/active';
import { said } from '@/integrations/api/client';
import { supabase } from '@/integrations/supabase/client';
import { forkSteps, stepsFromJson, stepsToJson } from '@/modules/processes/process-steps';
import { storeTerms } from '@/hooks/useTerms';
import type {
  Process, ProcessDocumentationMode, ProcessStatus, ProcessStep, ProcessVersion, ProcessVersionState,
} from '@/modules/processes/types';

/** The current version's publication date and link ride along for the list: staleness, and the open-document control. */
const PROCESS_SELECT = '*, current_version:process_versions!processes_current_version_id_fkey(published_at, source_url)';

function processFromRow(r: any): Process {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    teamId: r.team_id,
    title: r.title,
    ownerId: r.owner_id ?? null,
    status: r.status as ProcessStatus,
    documentationMode: r.documentation_mode as ProcessDocumentationMode,
    reviewIntervalMonths: r.review_interval_months ?? null,
    currentVersionId: r.current_version_id ?? null,
    lastReviewedAt: r.current_version?.published_at ?? null,
    currentSourceUrl: r.current_version?.source_url ?? null,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function versionFromRow(r: any): ProcessVersion {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    processId: r.process_id,
    versionNumber: r.version_number,
    state: r.state as ProcessVersionState,
    purpose: r.purpose ?? null,
    trigger: r.trigger ?? null,
    inputs: r.inputs ?? null,
    outputs: r.outputs ?? null,
    steps: stepsFromJson(r.steps),
    sourceUrl: r.source_url ?? null,
    changeSummary: r.change_summary ?? null,
    publishedAt: r.published_at ?? null,
    publishedBy: r.published_by ?? null,
    createdAt: r.created_at,
  };
}

const COLUMN_OF: Partial<Record<keyof Process, string>> = {
  title: 'title',
  ownerId: 'owner_id',
  status: 'status',
  reviewIntervalMonths: 'review_interval_months',
};

let _processesRequestGeneration = 0;
let _versionsRequestGeneration = 0;

interface ProcessesState {
  processes: Process[];
  /** Every version of a process, newest number first. Keyed by process id. */
  versionsByProcess: Record<string, ProcessVersion[]>;
  /**
   * Whether a process has an unpublished draft. Separate from
   * `versionsByProcess`, which only holds the process whose modal is open — the
   * list needs the answer for every row.
   */
  hasDraftByProcess: Record<string, boolean>;
  loading: boolean;
  workspaceId: string | null;

  /** Drop everything on an account change (#142). */
  reset: () => void;
  fetchProcesses: (workspaceId: string) => Promise<void>;
  addProcess: (args: { title: string; teamId: string; ownerId?: string | null }) => Promise<Process | null>;
  updateProcess: (id: string, updates: Partial<Process>) => Promise<void>;
  deleteProcess: (id: string) => Promise<void>;

  fetchVersions: (processId: string) => Promise<void>;
  /** The draft to edit — the existing one, or a fresh fork of what is published. */
  ensureDraft: (processId: string) => Promise<ProcessVersion | null>;
  /** Write-through edit of the draft — steps and context alike. */
  saveDraft: (versionId: string, patch: Partial<ProcessVersion>) => Promise<void>;
  discardDraft: (versionId: string) => Promise<void>;
  /** Supersede, promote and repoint in one transaction. Manager only. */
  publishDraft: (versionId: string, changeSummary: string | null) => Promise<boolean>;
  /** Write and publish a linked version in one transaction. Linked versions have no draft stage. */
  publishLink: (processId: string, sourceUrl: string, changeSummary: string | null) => Promise<boolean>;

}

async function refetchProcess(processId: string): Promise<Process | null> {
  const { data } = await supabase.from('processes').select(PROCESS_SELECT).eq('id', processId).maybeSingle();
  if (!data) return null;
  const saved = processFromRow(data);
  useProcessesStore.setState((s) => ({ processes: s.processes.map((p) => (p.id === processId ? saved : p)) }));
  return saved;
}


/** Processes-owned server cache, keyed by `workspaceId`. */
export const useProcessesStore = create<ProcessesState>((set, get) => ({
  processes: [],
  versionsByProcess: {},
  hasDraftByProcess: {},
  loading: false,
  workspaceId: null,

  reset: () => {
    _processesRequestGeneration += 1;
    _versionsRequestGeneration += 1;
    set({
      processes: [], versionsByProcess: {}, hasDraftByProcess: {}, loading: false, workspaceId: null,
    });
  },

  fetchProcesses: async (workspaceId) => {
    const requestGeneration = ++_processesRequestGeneration;
    const switchingWorkspace = get().workspaceId !== workspaceId;
    set({
      loading: true,
      workspaceId,
      ...(switchingWorkspace ? {
        processes: [], versionsByProcess: {}, hasDraftByProcess: {},
      } : {}),
    });
    const { data, error } = await supabase
      .from('processes')
      .select(PROCESS_SELECT)
      .eq('workspace_id', workspaceId)
      .order('title');

    if (
      requestGeneration !== _processesRequestGeneration
      || get().workspaceId !== workspaceId
    ) return;

    if (error) {
      console.error('[fetchProcesses] failed', error.message);
      set({ loading: false });
      return;
    }
    set({ processes: (data ?? []).map(processFromRow), loading: false });

    // Ids only — the badge is a yes/no. RLS scopes it, so a draft on a team the
    // caller cannot read never arrives.
    const { data: drafts } = await supabase
      .from('process_versions')
      .select('process_id')
      .eq('workspace_id', workspaceId)
      .eq('state', 'draft');

    if (
      requestGeneration !== _processesRequestGeneration
      || get().workspaceId !== workspaceId
    ) return;

    set({
      hasDraftByProcess: Object.fromEntries((drafts ?? []).map((d: any) => [d.process_id, true])),
    });
  },

  addProcess: async ({ title, teamId, ownerId = null }) => {
    const workspaceId = get().workspaceId;
    if (!workspaceId) return null;

    const now = new Date().toISOString();
    const optimistic: Process = {
      id: crypto.randomUUID(),
      workspaceId,
      teamId,
      title,
      ownerId,
      // A process starts as a draft and stays one until its first version is
      // published. Nothing is binding on the team until then.
      status: 'draft',
      documentationMode: 'native',
      reviewIntervalMonths: null,
      currentVersionId: null,
      lastReviewedAt: null,
      currentSourceUrl: null,
      createdAt: now,
      updatedAt: now,
    };
    set((s) => ({ processes: [...s.processes, optimistic] }));

    const { data, error } = await coreApi('/records/processes', {
      id: optimistic.id,
      workspace_id: workspaceId,
      team_id: teamId,
      title,
      owner_id: ownerId,
      status: 'draft',
    });

    if (error) {
      console.error('[addProcess] failed', error.message);
      set((s) => ({ processes: s.processes.filter((p) => p.id !== optimistic.id) }));
      const { withArticle } = storeTerms();
      storeNotice.error(
        error.code === '42501'
          ? `Only a manager of this team can create ${withArticle('process').toLowerCase()}.`
          : 'That could not be created.',
      );
      return null;
    }

    const saved = data?.[0] ? processFromRow(data[0]) : optimistic;
    set((s) => ({ processes: s.processes.map((p) => (p.id === saved.id ? saved : p)) }));
    return saved;
  },

  updateProcess: async (id, updates) => {
    const before = get().processes.find((p) => p.id === id);
    if (!before) return;

    set((s) => ({
      processes: s.processes.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    }));

    const row: Record<string, unknown> = {};
    for (const [key, column] of Object.entries(COLUMN_OF)) {
      const value = updates[key as keyof Process];
      if (value !== undefined) row[column!] = value;
    }
    if (Object.keys(row).length === 0) return;

    const { error } = await coreApi(`/records/processes/${id}`, row, 'PATCH');

    if (error) {
      console.error('[updateProcess] failed', error.message, error.details);
      set((s) => ({ processes: s.processes.map((p) => (p.id === id ? before : p)) }));
      // Archiving is manager-only and enforced by trigger, so a contributor
      // reaching it gets a message about the rung rather than a generic failure.
      const { withArticle } = storeTerms();
      storeNotice.error(
        said(error, 'archiving a process requires manager')
          ? `Only a manager of this team can archive ${withArticle('process').toLowerCase()}.`
          : said(error, 'review cadence requires')
            ? 'Only the owner or a manager of this team can set the review cadence.'
            : 'That change could not be saved.',
      );
    }
  },

  deleteProcess: async (id) => {
    const before = get().processes.find((p) => p.id === id);
    if (!before) return;

    set((s) => ({ processes: s.processes.filter((p) => p.id !== id) }));

    const { error } = await coreApi(`/records/processes/${id}`, {}, 'DELETE');

    if (error) {
      console.error('[deleteProcess] failed', error.message, error.details);
      set((s) => ({ processes: [...s.processes, before] }));
      const { withArticle } = storeTerms();
      storeNotice.error(
        error.code === '42501'
          ? `Only a manager of this team can delete ${withArticle('process').toLowerCase()}.`
          : 'That could not be deleted.',
      );
    }
  },

  fetchVersions: async (processId) => {
    const requestGeneration = ++_versionsRequestGeneration;
    const workspaceId = get().workspaceId;
    const { data, error } = await supabase
      .from('process_versions')
      .select('*')
      .eq('process_id', processId)
      .order('version_number', { ascending: false });

    if (
      requestGeneration !== _versionsRequestGeneration
      || get().workspaceId !== workspaceId
    ) return;

    if (error) {
      console.error('[fetchVersions] failed', error.message);
      return;
    }
    set((s) => ({
      versionsByProcess: { ...s.versionsByProcess, [processId]: (data ?? []).map(versionFromRow) },
    }));
  },

  ensureDraft: async (processId) => {
    const versions = get().versionsByProcess[processId] ?? [];
    const existing = versions.find((v) => v.state === 'draft');
    if (existing) return existing;

    const process = get().processes.find((p) => p.id === processId);
    if (!process) return null;

    const published = versions.find((v) => v.state === 'published');
    const highest = versions.reduce((max, v) => Math.max(max, v.versionNumber), 0);

    // Step ids come across verbatim, so a step that survives into the next
    // version stays the same step for issue links and, later, acknowledgements.
    // The context forks with them: a draft starts as a copy of what is live,
    // not as a blank page beside steps that already say something.
    const steps = published ? forkSteps(published.steps) : [];

    const { data, error } = await coreApi('/records/process-versions', {
      workspace_id: process.workspaceId,
      process_id: processId,
      version_number: highest + 1,
      state: 'draft',
      steps: stepsToJson(steps),
      purpose: published?.purpose ?? null,
      trigger: published?.trigger ?? null,
      inputs: published?.inputs ?? null,
      outputs: published?.outputs ?? null,
    });

    if (error || !data?.[0]) {
      console.error('[ensureDraft] failed', error?.message ?? 'no row returned');
      storeNotice.error('A draft could not be started.');
      return null;
    }

    const draft = versionFromRow(data[0]);
    set((s) => ({
      versionsByProcess: {
        ...s.versionsByProcess,
        [processId]: [draft, ...(s.versionsByProcess[processId] ?? [])],
      },
      hasDraftByProcess: { ...s.hasDraftByProcess, [processId]: true },
    }));
    return draft;
  },

  saveDraft: async (versionId, patch) => {
    const entry = Object.entries(get().versionsByProcess)
      .find(([, list]) => list.some((v) => v.id === versionId));
    if (!entry) return;
    const [processId, list] = entry;
    const before = list.find((v) => v.id === versionId)!;

    set((s) => ({
      versionsByProcess: {
        ...s.versionsByProcess,
        [processId]: (s.versionsByProcess[processId] ?? []).map((v) =>
          v.id === versionId ? { ...v, ...patch } : v,
        ),
      },
    }));

    const row: Record<string, unknown> = {};
    if (patch.steps !== undefined) row.steps = stepsToJson(patch.steps);
    if (patch.purpose !== undefined) row.purpose = patch.purpose;
    if (patch.trigger !== undefined) row.trigger = patch.trigger;
    if (patch.inputs !== undefined) row.inputs = patch.inputs;
    if (patch.outputs !== undefined) row.outputs = patch.outputs;
    if (Object.keys(row).length === 0) return;

    const { error } = await coreApi(`/records/process-versions/${versionId}`, row, 'PATCH');

    if (error) {
      console.error('[saveDraft] failed', error.message, error.details);
      set((s) => ({
        versionsByProcess: {
          ...s.versionsByProcess,
          [processId]: (s.versionsByProcess[processId] ?? []).map((v) =>
            v.id === versionId ? before : v,
          ),
        },
      }));
      // The freeze trigger is the likely cause: a version that stopped being a
      // draft between opening the editor and saving cannot take the edit.
      storeNotice.error(
        said(error, 'can no longer be edited')
          ? 'This version has been published and can no longer be edited.'
          : 'Those steps could not be saved.',
      );
    }
  },

  discardDraft: async (versionId) => {
    const entry = Object.entries(get().versionsByProcess)
      .find(([, list]) => list.some((v) => v.id === versionId));
    if (!entry) return;
    const [processId, list] = entry;
    const before = list.find((v) => v.id === versionId)!;

    set((s) => ({
      versionsByProcess: {
        ...s.versionsByProcess,
        [processId]: (s.versionsByProcess[processId] ?? []).filter((v) => v.id !== versionId),
      },
      hasDraftByProcess: { ...s.hasDraftByProcess, [processId]: false },
    }));

    const { error } = await coreApi(`/records/process-versions/${versionId}`, {}, 'DELETE');

    if (error) {
      console.error('[discardDraft] failed', error.message, error.details);
      set((s) => ({
        versionsByProcess: {
          ...s.versionsByProcess,
          [processId]: [before, ...(s.versionsByProcess[processId] ?? [])],
        },
        hasDraftByProcess: { ...s.hasDraftByProcess, [processId]: true },
      }));
      storeNotice.error('That draft could not be discarded.');
    }
  },

  publishDraft: async (versionId, changeSummary) => {
    const entry = Object.entries(get().versionsByProcess)
      .find(([, list]) => list.some((v) => v.id === versionId));
    if (!entry) return false;
    const [processId, list] = entry;
    const draft = list.find((v) => v.id === versionId);
    const process = get().processes.find((p) => p.id === processId);
    if (!draft || !process) return false;

    // No optimistic write. Publishing moves three rows through states the
    // database enforces, and guessing the outcome here would mean showing a
    // published version that the manager rung may still refuse.
    const { error } = await coreApi(`/process-versions/${versionId}/publish`, {
      change_summary: changeSummary?.trim() || null,
    });

    if (error) {
      console.error('[publishDraft] failed', error.message, error.details);
      storeNotice.error(
        error.code === '42501' || said(error, 'requires manager')
          ? 'Only a manager of this team can publish a version.'
          : 'That version could not be published.',
      );
      return false;
    }

    await get().fetchVersions(processId);
    set((s) => ({ hasDraftByProcess: { ...s.hasDraftByProcess, [processId]: false } }));
    await refetchProcess(processId);
    return true;
  },

  publishLink: async (processId, sourceUrl, changeSummary) => {
    const process = get().processes.find((p) => p.id === processId);
    if (!process) return false;

    const { error } = await coreApi(`/processes/${processId}/publish-link`, {
      source_url: sourceUrl,
      change_summary: changeSummary?.trim() || null,
    });

    if (error) {
      console.error('[publishLink] failed', error.message, error.details);
      storeNotice.error(
        error.code === '42501' || said(error, 'requires manager')
          ? 'Only a manager of this team can publish a version.'
          : said(error, 'source_url_check')
            ? 'That link is not a Google Workspace or Microsoft 365 address.'
            : 'That version could not be published.',
      );
      return false;
    }

    await get().fetchVersions(processId);
    set((s) => ({ hasDraftByProcess: { ...s.hasDraftByProcess, [processId]: false } }));
    await refetchProcess(processId);
    return true;
  },

}));
